import React, { useState, useEffect } from 'react';
import { Search, BookOpen, Clock, Hash, Plus, Edit2, Trash2, Eye, Printer } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import EmptyState from '../common/EmptyState';
import Modal from '../Modal';
import FormInput from '../FormInput';
import ConfirmationModal from '../ConfirmationModal';

const MatieresTab = () => {
  const [matieres, setMatieres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedMatiere, setSelectedMatiere] = useState(null);
  const [detailMatiere, setDetailMatiere] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    code: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [matiereToDelete, setMatiereToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  const fetchMatieres = async () => {
    setLoading(true);
    setError('');
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/concours/matieres/?${queryParams}`);
      
      // Debug : log de la réponse complète
      console.log('Response complet:', response.data);
      
      if (response.data) {
        // Gestion de la structure de données personnalisée du backend
        let responseData = [];
        let paginationData = null;
        
        if (response.data.data) {
          // Structure avec pagination personnalisée
          responseData = response.data.data;
          paginationData = response.data.pagination;
          
          // Compléter les propriétés manquantes si nécessaire
          if (paginationData && !paginationData.has_next) {
            paginationData.has_next = paginationData.current_page < paginationData.total_pages;
          }
          if (paginationData && !paginationData.has_previous) {
            paginationData.has_previous = paginationData.current_page > 1;
          }
        } else if (response.data.results) {
          // Structure DRF standard
          responseData = response.data.results;
          paginationData = {
          count: response.data.count || 0,
          total_pages: Math.ceil((response.data.count || 0) / pageSize),
          current_page: currentPage,
            page_size: pageSize,
            has_next: !!response.data.next,
            has_previous: !!response.data.previous
          };
        } else {
          // Pas de pagination, toutes les données
          responseData = Array.isArray(response.data) ? response.data : [];
          paginationData = {
            count: responseData.length,
            total_pages: Math.ceil(responseData.length / pageSize),
            current_page: 1,
            page_size: pageSize,
            has_next: false,
            has_previous: false
          };
        }
        
        console.log('Données traitées:', responseData);
        console.log('Pagination traitée:', paginationData);
        
        setMatieres(Array.isArray(responseData) ? responseData : []);
        setPagination(paginationData);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des matières');
      setMatieres([]);
      setPagination(null);
      toast.error('Impossible de charger les matières');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchMatieres();
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [currentPage, pageSize, searchTerm]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal de création/modification
  const handleOpenModal = (matiere = null) => {
    if (matiere) {
      setSelectedMatiere(matiere);
      setFormData({
        nom: matiere.nom || '',
        code: matiere.code || '',
        description: matiere.description || ''
      });
    } else {
      setSelectedMatiere(null);
      setFormData({
        nom: '',
        code: '',
        description: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Ouvrir le modal de détails
  const handleOpenDetailModal = (matiere) => {
    setDetailMatiere(matiere);
    setIsDetailModalOpen(true);
  };

  // Fonction d'impression
  const handlePrint = () => {
    window.print();
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom.trim()) {
      errors.nom = "Le nom est requis";
    }
    if (!formData.code.trim()) {
      errors.code = "Le code est requis";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const dataToSend = {
        ...formData
      };

      if (selectedMatiere) {
        // Mise à jour
        await api.put(`/concours/matieres/${selectedMatiere.id}/`, dataToSend);
        toast.success("Matière mise à jour avec succès");
      } else {
        // Création
        await api.post('/concours/matieres/', dataToSend);
        toast.success("Matière créée avec succès");
      }

      fetchMatieres();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
      
      let errorMsg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'object') {
          const errorMessages = Object.entries(responseData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMsg = errorMessages || errorMsg;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (matiere) => {
    setMatiereToDelete(matiere);
    setConfirmConfig({
      title: "Supprimer la matière",
      message: `Êtes-vous sûr de vouloir supprimer la matière "${matiere.nom}" ?`,
      confirmText: "Supprimer",
      type: "danger"
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!matiereToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/concours/matieres/${matiereToDelete.id}/`);
      toast.success("Matière supprimée avec succès");
      fetchMatieres();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer cette matière. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  // Rendering du footer du modal de création/modification
  const renderModalFooter = () => (
    <div className="flex justify-end space-x-4">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsModalOpen(false)}
      >
        Annuler
      </button>
      <button
        type="button"
        className={`inline-flex justify-center px-6 py-2.5 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#161c3f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Traitement...
          </>
        ) : (
          selectedMatiere ? 'Mettre à jour' : 'Créer'
        )}
      </button>
    </div>
  );

  // Rendering du footer du modal de détails
  const renderDetailModalFooter = () => (
    <div className="flex justify-between">
      <button
        type="button"
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={handlePrint}
      >
        <Printer className="h-4 w-4 mr-2" />
        Imprimer
      </button>
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsDetailModalOpen(false)}
      >
        Fermer
      </button>
    </div>
  );

  if (loading && matieres.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher une matière..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle Matière
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {matieres.length === 0 && !loading ? (
        <EmptyState
          icon={BookOpen}
          title="Aucune matière"
          description="Aucune matière trouvée."
          actionLabel="Créer une matière"
          onAction={() => handleOpenModal()}
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Matière
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {matieres.map((matiere) => (
                  <tr key={matiere.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                            <BookOpen className="h-5 w-5 text-purple-600" />
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{matiere.nom}</div>
                          {matiere.description && (
                            <div className="text-sm text-gray-500 truncate">{matiere.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 font-mono">
                        {matiere.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleOpenDetailModal(matiere)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenModal(matiere)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(matiere)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedMatiere ? 'Modifier la matière' : 'Nouvelle matière'}
        footer={renderModalFooter()}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Nom de la matière"
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              placeholder="Ex: Mathématiques"
              required
              error={formErrors.nom}
              icon={BookOpen}
            />
            
            <FormInput
              label="Code de la matière"
              type="text"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              placeholder="Ex: MATH"
              required
              error={formErrors.code}
              maxLength={10}
              style={{ fontFamily: 'monospace' }}
            />
          </div>

          <FormInput
            label="Description"
            type="textarea"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Description de la matière..."
            error={formErrors.description}
            rows={3}
          />

          <div className="text-sm text-gray-500 mt-4">
            <span className="text-red-500">*</span> Champs obligatoires
          </div>
        </form>
      </Modal>

      {/* Modal de détails */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Détails de la matière"
        footer={renderDetailModalFooter()}
        maxWidth="max-w-2xl"
      >
        {detailMatiere && (
          <div className="space-y-6 print:space-y-4">
            {/* En-tête pour l'impression */}
            <div className="hidden print:block text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Détails de la Matière</h1>
              <p className="text-gray-600 mt-2">Généré le {new Date().toLocaleDateString('fr-FR')}</p>
            </div>

            {/* Informations générales */}
            <div className="bg-gray-50 p-6 rounded-lg print:bg-white print:border print:border-gray-300">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BookOpen className="h-5 w-5 mr-2 text-purple-600" />
                Informations générales
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom de la matière</label>
                  <p className="mt-1 text-sm text-gray-900">{detailMatiere.nom}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Code</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{detailMatiere.code}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            {detailMatiere.description && (
              <div className="bg-blue-50 p-6 rounded-lg print:bg-white print:border print:border-gray-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{detailMatiere.description}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer la matière"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer cette matière ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default MatieresTab; 